import React, { useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import { FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';
import { useHistory } from 'react-router';
import { useWolfTracsState } from '../../hooks/redux/useWolfTracsState';
import { EnrollmentPackageId, PersonId } from '../../types/api/primaryKeys/PrimaryKey';
import { ReactFC } from '../../types/react/ReactFC';
import { useForm } from 'react-hook-form';
import { Nullable } from '../../types/custom/Nullable';
import { getEmployeePackagePersonData, updateEmployee } from '../../apiCalls/employees/employeeApiCalls';
import { useReduxDispatch } from '../../hooks/redux/useReduxDispatch';
import { fetchUpdatePackageProgress } from '../../actions/EnrollmentActions';
import { stepNumberHelper } from '../../utils/StepNumberHelper';
import { PackageStepType } from '../../types/api/enrollmentPackages/Package';
import { useInitialDataFromApiCall } from '../../hooks/api/apiHooks';
import { LoadingBlock } from '../LoadingBlock';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import {
    // @ts-expect-error Don't have time to get types for this
    formatDate,
    // @ts-expect-error Don't have time to get types for this
    parseDate
} from 'react-day-picker/moment';
import { EmployeeDetail } from '../../types/api/employees/EmployeeDetail';
import { useEffect } from 'react';
import { ApiDate } from '../../types/custom/ApiDate';

interface Props {
    packageId: EnrollmentPackageId;
    employeePersonId: PersonId;
}

interface FormAddress {
    street: string;
    streetSecondary: Nullable<string>;
    city: string;
    state: string;
    postalCode: string;
}
interface FormInputs {
    nameFirst: string;
    nameMiddle: Nullable<string>;
    nameLast: string;
    phonePrimary: string;
    emailPrimary: string;
    emailAlternate: string;
    employerEmployeeIdentifier: string;
    usSocialSecurityNumber: string;
    gender: string;
    addressMailing: FormAddress;
}

/**
 * I'm just going to say it; this absolutely sucks. It sucks that there is STILL not a sane, application-wide proper handling of dates.
 * I added this BECAUSE simply calling the Date constructor on a date in format (yyyy/mm/dd) results in a LOCAL timezone conversion first, essentially setting that date BACK
 * an entire day. This dumb code manually makes it take the parts as explicitly laid out, ignoring time zone.
 * I don't like it. You don't like it. But this is the quick fix for this file. We really need consistency for date handling, but there is none.
 * @param date 
 * @returns 
 */
const parseYearMonthDayDateWithoutTimezoneConversion = (date: ApiDate) => {
    const parts = date.split('-');
    // Please pay attention to the month (parts[1]); JavaScript counts months from 0:
    // January - 0, February - 1, etc.
    const newDate = new Date(Number.parseInt(parts[0]), Number.parseInt(parts[1]) - 1, Number.parseInt(parts[2])); 

    return newDate;
};

// Note: this format is not ideal. But I was able to actually copy/paste these in this way. Refactoring would take time.
const stateAbbrev = ['AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FM', 'FL', 'GA', 'GU', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MH', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'MP', 'OH', 'OK', 'OR', 'PW', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VI', 'VA', 'WA', 'WV', 'WI', 'WY'] as const;
const stateNames = ['Alabama', 'Alaska', 'American Samoa', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'District Of Columbia', 'Federated States Of Micronesia', 'Florida', 'Georgia', 'Guam', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Marshall Islands', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Northern Mariana Islands', 'Ohio', 'Oklahoma', 'Oregon', 'Palau', 'Pennsylvania', 'Puerto Rico', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virgin Islands', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'] as const;

export const EmployeeDetailNewForm: ReactFC<Props> = ({ packageId, employeePersonId }) => {
    const { data: activePackage } = useWolfTracsState(s => s.packages.activePackage);
    const history = useHistory();

    const [ birthday, setBirthday ] = useState<Nullable<Date>>(null);

    const { data: employeeData, isLoading } = useInitialDataFromApiCall(() => getEmployeePackagePersonData(packageId, employeePersonId), [packageId, employeePersonId]);

    // Because birthday is going into state, this has to be set after the API call comes in separately.
    useEffect(() => {
        if (employeeData !== null && employeeData.dateOfBirth !== null) {
            setBirthday(parseYearMonthDayDateWithoutTimezoneConversion(employeeData.dateOfBirth));
        }

    }, [employeeData]);

    const { register, handleSubmit, formState: { errors } } = useForm<FormInputs>();

    const dispatch = useReduxDispatch();

    const onSubmit = async (data: FormInputs) => {
        // Shouldn't be possible.
        if (!activePackage) {
            return;
        }

        if (birthday === null) {
            alert('Date of birth is required.');
            return;
        }

        const postData = {
            ...data,
            dateOfBirth: birthday.toISOString()
        };

        // I DON'T LIKE This type assertion, but don't have time to type it correctly. ToDo: Circle back.
        await updateEmployee(packageId, activePackage.personId, postData as EmployeeDetail);

        const currentStepNumber = stepNumberHelper.stepNumberForNamedStep(activePackage, PackageStepType.EmployeeInfo);
        await dispatch(fetchUpdatePackageProgress(activePackage.enrollmentPackageId, currentStepNumber, true));

        history.push(`/packages/${packageId}/dependents`);
    };

    const onCancel = () => {
        history.push(`/packages/${packageId}/welcome`);
    };

    if (isLoading || employeeData === null) {
        return <LoadingBlock />;
    }

    return (
        <Card>
            <Card.Body>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Form.Group controlId="first-name">
                        <Form.Label>First/Given Name*</Form.Label>
                        <Form.Control required defaultValue={employeeData.nameFirst} type="text" {...register('nameFirst', { required: true })} />
                    </Form.Group>

                    <Form.Group controlId="middle-name">
                        <Form.Label>Middle Name (if any)</Form.Label>
                        <Form.Control defaultValue={employeeData.nameMiddle ?? ''} type="text"  {...register('nameMiddle', { required: false })}/>
                    </Form.Group>

                    <Form.Group controlId="last-name">
                        <Form.Label>Last/Family Name*</Form.Label>
                        <Form.Control required defaultValue={employeeData.nameLast} type="text"  {...register('nameLast', { required: true })}/>
                    </Form.Group>

                    <Form.Group controlId="birthday">
                        <Form.Label>Date of Birth*</Form.Label>
                        <div>
                            <DayPickerInput
                                inputProps={{
                                    className: 'form-control',
                                    required: true
                                }}
                                format={'MM/DD/YYYY'}
                                formatDate={formatDate}
                                parseDate={parseDate}
                                placeholder={"MM/DD/YYYY"}
                                onDayChange={date => setBirthday(date)}

                                value={birthday ?? ''}
                            />
                        </div>
                    </Form.Group>

                    <Form.Group controlId="employer-employee-identifier">
                        <Form.Label>UO Student ID number*</Form.Label>
                        <Form.Text>This number begins with &quot;95&quot; and is 9 digits long.</Form.Text>
                        <Form.Control required defaultValue={employeeData.employerEmployeeIdentifier ?? ''} type="text" {...register('employerEmployeeIdentifier', { required: true, minLength: 9, maxLength: 9, pattern: /^95/ })}/>

                        {errors.employerEmployeeIdentifier && 
                            <div className="text-danger">
                                Please validate that your student ID is in the correct format.
                            </div>
                        }
                    </Form.Group>

                    <Form.Group controlId="social-security-number">
                        <Form.Label>U.S. Social Security Number*</Form.Label>
                        <Form.Text>If you have not been issued a Social Security Number yet, please enter your UO Student ID Number.</Form.Text>
                        <Form.Control required defaultValue={employeeData.usSocialSecurityNumber} type="text" {...register('usSocialSecurityNumber', { required: true })}/>
                    </Form.Group>

                    <Form.Group controlId="gender">
                        <Form.Label>Your sex* is</Form.Label>
                        <Form.Text muted>* The GTFF and the University of Oregon strive to welcome all members of our community, including those who do not identify as either Female or Male.</Form.Text>
                        <Form.Text muted>Our community profoundly laments that Oregon law compels us to require this question and to accept only &quot;Female&quot; or &quot;Male&quot; as valid responses.</Form.Text>
                        <Form.Control required as="select" defaultValue={employeeData.gender ?? ''} {...register('gender', { required: true })}>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                        </Form.Control>
                    </Form.Group>

                    <Form.Group controlId="primary-phone">
                        <Form.Label>Personal phone number</Form.Label>
                        <Form.Text>(If you are new to the United States and do not yet have a US phone number, leave blank.)</Form.Text>
                        <Form.Control type="text" defaultValue={employeeData.phonePrimary ?? ''} {...register('phonePrimary', { required: false })}/>
                    </Form.Group>

                    <Form.Group controlId="alternate-email">
                        <Form.Label>Personal email address*</Form.Label>
                        <Form.Control required defaultValue={employeeData.emailAlternate ?? ''} type="email" {...register('emailAlternate', { required: true })}/>
                    </Form.Group>

                    <Form.Group controlId="primary-email">
                        <Form.Label>uoregon.edu email address*</Form.Label>
                        <Form.Control required defaultValue={employeeData.emailPrimary ?? ''} type="email" {...register('emailPrimary', { required: true, pattern: /@uoregon.edu$/ })}/>

                        {errors.emailPrimary && 
                            <div className="text-danger">
                                Please validate that your uoregon.edu email is in the correct format.
                            </div>
                        }
                    </Form.Group>

                    <legend>Address (Mailing)*</legend>

                    <Form.Group controlId="address-mailing-street">
                        <Form.Label>Street*</Form.Label>
                        <Form.Control required type="text" defaultValue={employeeData.addressMailing.street ?? ''} {...register('addressMailing.street', { required: true })} />
                    </Form.Group>

                    <Form.Group controlId="address-mailing-street-secondary">
                        <Form.Label>Street 2</Form.Label>
                        <Form.Control type="text" defaultValue={employeeData.addressMailing.streetSecondary ?? ''} {...register('addressMailing.streetSecondary')} />
                    </Form.Group>

                    <Form.Group controlId="address-mailing-city">
                        <Form.Label>City*</Form.Label>
                        <Form.Control required type="text" defaultValue={employeeData.addressMailing.city ?? ''} {...register('addressMailing.city', { required: true })} />
                    </Form.Group>

                    <Form.Group controlId="address-mailing-state">
                        <Form.Label>State*</Form.Label>
                        <Form.Control required as="select" defaultValue={employeeData.addressMailing.state ?? ''} {...register('addressMailing.state', { required: true })}>
                                {stateAbbrev.map((abbrev, idx) => (
                                    <option key={abbrev} value={abbrev}>{stateNames[idx]}</option>
                                ))}
                        </Form.Control>
                    </Form.Group>

                    <Form.Group controlId="address-mailing-postal-code">
                        <Form.Label>Postal Code*</Form.Label>
                        <Form.Control required type="text" defaultValue={employeeData.addressMailing.postalCode ?? ''} {...register('addressMailing.postalCode', { required: true })} />
                    </Form.Group>

                    <Button disabled={activePackage === null} type="button" className="mr-2" onClick={onCancel}><FaAngleDoubleLeft />Back</Button>
                    <Button disabled={activePackage === null} type="submit" className="float-right">Save &amp; Continue<FaAngleDoubleRight /></Button>
                </Form>
            </Card.Body>
        </Card>
    );
};